import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IPatientTriggeredAlert } from "../../../../@types/admin/AdminPatient";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { humanizeEnumName } from "../../../../utils/enumNames";
import { StatusBadge } from "../AdminPage.styles";

/** Search fields that go with the columns below. */
export const TRIGGERED_ALERT_SEARCH_FIELDS: (keyof IPatientTriggeredAlert)[] = [
  "triggerName",
  "alertSummary",
  "alertStatus",
  "type",
];

/**
 * Columns for a patient's FIRED alerts — Retool's `modulePatientAlerts`.
 * Shared by the Patients page's Monitoring tab and the Alerts page's
 * "Triggered alerts" dialog (EN-5146), so the two read identically.
 */
export function useTriggeredAlertColumns(): ColumnDef<
  IPatientTriggeredAlert,
  string
>[] {
  return useMemo(
    () => [
      {
        accessorKey: "timeOfAlert",
        header: "Time",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.timeOfAlert, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "type",
        header: "Type",
        accessorFn: (alert) => humanizeEnumName(alert.type) || "-",
        enableSorting: true,
      },
      { accessorKey: "triggerName", header: "Trigger", enableSorting: true },
      { accessorKey: "value", header: "Value", enableSorting: true },
      { accessorKey: "alertSummary", header: "Summary", enableSorting: true },
      { accessorKey: "timeFrame", header: "Time frame", enableSorting: true },
      {
        id: "alertStatus",
        header: "Status",
        accessorFn: (alert) => humanizeEnumName(alert.alertStatus) || "-",
        cell: ({ row }) => {
          const label = humanizeEnumName(row.original.alertStatus);
          return (
            <StatusBadge
              status={row.original.resolvedAt ? "inactive" : "warning"}
            >
              {label || "-"}
            </StatusBadge>
          );
        },
        enableSorting: true,
      },
      { accessorKey: "alertMethod", header: "Method", enableSorting: true },
      { accessorKey: "resolvedBy", header: "Resolved by", enableSorting: true },
      {
        accessorKey: "resolvedAt",
        header: "Resolved at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.resolvedAt, "-")}</span>
        ),
        enableSorting: true,
      },
    ],
    []
  );
}
